/**
 * Freshness rendering (spec section 64).
 *
 * There is deliberately no component here that renders a single global "last
 * updated". Jeabs refreshes a player profile on a ~10 minute clock, kills and
 * standings on a ~6 hour ranking-board clock, and hero gear on its own — so one
 * combined timestamp would be true of nothing on the screen. `FreshnessStrip`
 * is the only way to show a player's freshness, and it takes all four clocks.
 *
 * The same rule holds per locale: the Korean labels are five distinct words
 * (프로필 / 처치 수 / 순위 / 영웅 장비 / 연맹 명단), never one "업데이트".
 * Collapsing them in translation would undo section 64 as effectively as
 * collapsing them in code.
 */

import type { PlayerSummary } from '@kso/schemas';
import { useT } from '../../i18n';
import type { Dictionary } from '../../i18n';

type FreshnessInfo = PlayerSummary['freshness']['profile'];

function bucketExplanation(t: Dictionary, bucket: string): string {
  const buckets: Record<string, string> = t.freshness.buckets;
  return buckets[bucket] ?? '';
}

export function FreshnessBadge({ label, info }: { label: string; info: FreshnessInfo }) {
  const t = useT();
  return (
    <span
      className="freshness-item"
      title={t.freshness.badgeTitle(label, info.label, bucketExplanation(t, info.bucket))}
    >
      <span className={`freshness-item__dot freshness-dot--${info.bucket}`} aria-hidden="true" />
      <span className="freshness-item__label">{label}</span>
      <span className="freshness-item__age">
        {info.bucket === 'UNKNOWN' ? t.freshness.never : info.label}
      </span>
    </span>
  );
}

/**
 * @param sources One entry per upstream clock. Callers pass every clock that
 *   feeds the values on screen; collapsing them is the failure this component
 *   exists to prevent.
 */
export function FreshnessStrip({
  sources,
}: {
  sources: ReadonlyArray<{ label: string; info: FreshnessInfo }>;
}) {
  const t = useT();
  return (
    <div className="freshness-strip" aria-label={t.freshness.stripLabel}>
      {sources.map((source) => (
        <FreshnessBadge key={source.label} label={source.label} info={source.info} />
      ))}
    </div>
  );
}

/**
 * The four player clocks the API reports, plus the alliance roster clock when
 * the caller has it (it comes from a different request, so it is optional
 * rather than faked).
 */
export function PlayerFreshnessStrip({
  freshness,
  allianceRoster,
}: {
  freshness: PlayerSummary['freshness'];
  allianceRoster?: FreshnessInfo | null;
}) {
  const t = useT();
  const sources: Array<{ label: string; info: FreshnessInfo }> = [
    { label: t.freshness.sources.profile, info: freshness.profile },
    { label: t.freshness.sources.kills, info: freshness.kills },
    { label: t.freshness.sources.standings, info: freshness.standings },
    { label: t.freshness.sources.heroGear, info: freshness.heroGear },
  ];
  if (allianceRoster) {
    sources.push({ label: t.freshness.sources.allianceRoster, info: allianceRoster });
  }
  return <FreshnessStrip sources={sources} />;
}
